import React, { useState } from 'react';
import PropTypes from 'prop-types';
import PersonalizedCardGeneral, { PersonalizedCardGeneralShape } from './personalized-card-general';
import PersonalizedCardMobile, { PersonalizedCardMobileShape } from './personalized-card-mobile';
import PersonalizedCardMobileBroadband, { PersonalizedCardMobileBroadbandShape } from './personalized-card-mobile-broadband';
import PersonalizedCardMobileHomeBroadband, { PersonalizedCardMobileHomeBroadbandShape } from './personalized-card-mobile-home-broadband';
import PersonalizedCardMobileSummary, { PersonalizedCardMobileSummaryShape } from './personalized-card-mobile-summary';
import PersonalizedCardBroadband, { PersonalizedCardBroadbandShape } from './personalized-card-broadband';
import PersonalizedCardIptv, { PersonalizedCardIptvShape } from './personalized-card-iptv';
import PersonalizedCardMiptv, { PersonalizedCardMiptvShape } from './personalized-card-miptv';
import PersonalizedCardAiptv, { PersonalizedCardAiptvShape } from './personalized-card-aiptv';
import PersonalizedCardTve, { PersonalizedCardTveShape } from './personalized-card-tve';
import PersonalizedCardCableTv, { PersonalizedCardCableTvShape } from './personalized-card-cable-tv';
import { Div, Title, GridBag, Gutter, Button, Breakpoint } from '@gaiads/telia-react-component-library';

const PersonalizedCardList = ({ title, titleColor, showMore, showLess, cards, narrowLayout, cardsLayout }) => {
  const [showAllCards, setShowAllCards] = useState(false);

  const getPersonalizedCard = (card) => {
    switch (card.type) {
      case 'general':
        return <PersonalizedCardGeneral {...card} />;
      case 'mobile':
        return <PersonalizedCardMobile {...card} />;
      case 'mobileBroadband':
        return <PersonalizedCardMobileBroadband {...card} />;
      case 'mobileHomeBroadband':
        return <PersonalizedCardMobileHomeBroadband {...card} />;
      case 'mobileSummary':
        return <PersonalizedCardMobileSummary {...card} />;
      case 'broadband':
        return <PersonalizedCardBroadband {...card} />;
      case 'iptv':
        return <PersonalizedCardIptv {...card} />;
      case 'miptv':
        return <PersonalizedCardMiptv {...card} />;
      case 'aiptv':
        return <PersonalizedCardAiptv {...card} />;
      case 'tve':
        return <PersonalizedCardTve {...card} />;
      case 'cableTv':
        return <PersonalizedCardCableTv {...card} />;
      default:
        return null;
    }
  };

  const renderCardItem = (card) => {
    const personalizedCard = getPersonalizedCard(card);

    const cardListBreakpoints = narrowLayout ?
      { xlg: 4, sm: 6, xs: 12 } :
      { md: 4, sm: 6, xs: 12 };

    return (
      <GridBag.Item {...cardListBreakpoints}>
        {personalizedCard}
      </GridBag.Item>
    );
  };

  const renderWithInitialNumberOfCards = (initialNumberOfCards) => {
    // Because blue400 is not officially supported as text color in the component library
    const titleColorProp = titleColor === 'blue400' ?
      { style: { color: '#4dc0ff' } } :
      { color: titleColor };

    return (
      <Div>
        {title &&
          <Title size="md" {...titleColorProp} margin={{ bottom: 'md', top: 'zero' }}>
            {title}
          </Title>
        }

        <GridBag>
          {cards &&
            cards.slice(0, initialNumberOfCards).map((card) => {
              return renderCardItem(card);
            })}
        </GridBag>

        {cards && cards.length > initialNumberOfCards && (
          <React.Fragment>
            {showAllCards && (
              <React.Fragment>
                { /* TRCL: GridBag item margins are not standard component library sizes. Will have to change this if the gridbag component changes. */ }
                <Div className="cms-personalized-card-list__gridbag-gutter-simulator" />

                <GridBag>
                  {cards.slice(initialNumberOfCards).map((card) => {
                    return renderCardItem(card);
                  })}
                </GridBag>
              </React.Fragment>
            )}

            <Gutter size="md" />

            <Div alignTextToCenter>
              <Button variant="secondary" size="xl" onClick={() => setShowAllCards(!showAllCards)}>
                {showAllCards ? showLess : showMore}
              </Button>
            </Div>
          </React.Fragment>
        )}
      </Div>
    );
  };

  if (cards && cards.length > 0) {
    return (
      <React.Fragment>
        <Breakpoint narrowerThan="sm">
          { renderWithInitialNumberOfCards(cardsLayout.mobile) }
        </Breakpoint>

        <Breakpoint at="sm">
          { renderWithInitialNumberOfCards(cardsLayout.tablet) }
        </Breakpoint>

        <Breakpoint widerThan="md">
          { renderWithInitialNumberOfCards(cardsLayout.desktop) }
        </Breakpoint>
      </React.Fragment>
    );
  }

  return null;
};

PersonalizedCardList.defaultProps = {
  narrowLayout: false,
  cardsLayout: { mobile: 2, tablet: 4, desktop: 6 },
};

PersonalizedCardList.propTypes = {
  title: PropTypes.string.isRequired,
  titleColor: PropTypes.string.isRequired,
  showMore: PropTypes.string.isRequired,
  showLess: PropTypes.string.isRequired,
  cards: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape(PersonalizedCardGeneralShape),
      PropTypes.shape(PersonalizedCardMobileShape),
      PropTypes.shape(PersonalizedCardMobileBroadbandShape),
      PropTypes.shape(PersonalizedCardMobileHomeBroadbandShape),
      PropTypes.shape(PersonalizedCardMobileSummaryShape),
      PropTypes.shape(PersonalizedCardBroadbandShape),
      PropTypes.shape(PersonalizedCardIptvShape),
      PropTypes.shape(PersonalizedCardMiptvShape),
      PropTypes.shape(PersonalizedCardAiptvShape),
      PropTypes.shape(PersonalizedCardTveShape),
      PropTypes.shape(PersonalizedCardCableTvShape),
    ]),
  ).isRequired,
  narrowLayout: PropTypes.boolean,
  cardsLayout: PropTypes.shape({
    mobile: PropTypes.number.isRequired,
    tablet: PropTypes.number.isRequired,
    desktop: PropTypes.number.isRequired,
  }),
};

export default PersonalizedCardList;
